import React, { useCallback, useEffect, useMemo, useState } from "react";
import CustomBreadcrumbs from "../../components/CustomBreadcrumbs";
import { Backdrop, Box, CircularProgress, Typography } from "@mui/material";
import "./Invoice.css";
import ReportFilter from "../Reports/ReportFilter";
import InvoiceSummary from "./InvoiceSummary";
import InvoiceDescription from "./InvoiceDescription";
import { useDispatch, useSelector } from "react-redux";
import functions from "../../utils/Api";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";
import { getLocationName, getMealName } from "../../utils/mealUtils";
import Toaster from "../../components/Toaster/Toaster";
import { setToasterStatusMsg } from "../../Redux/reportSlice";
import logoUrl from "../../Assets/images/Logo.jpg";

const InvoicePage = () => {
  const dispatcher = useDispatch();
  const { mealType, location, startDate, endDate } = useSelector(
    (state) => state.report
  );
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [invoiceDescriptionData, setInvoiceDescriptionData] = useState([]);
  const [invoiceDescriptionDataByLocation, setInvoiceDescriptionByLocation] =
    useState(null);
  const [chargedPlateCount, setChargedPlateCount] = useState(0);
  const [totalInvoice, setTotalInvoice] = useState(0);
  const [totalMealConsumed, setTotalMealConsumed] = useState(0);
  const [vendorIdByLocation, setvendorIdByLocation] = useState([]);
  const [invoiceSummaryData, setInvoiceSummaryData] = useState(null);
  const [invoiceDetailsByLocation, setInvoiceDetailsByLocation] =
    useState(null);
  const [perPlatePrice, setPerPlatePrice] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ApiStatus, setApiStatus] = useState(false);
  const [minGuarantee, setMinGuarantee] = useState(0);
  const [vendorId, setVendorId] = useState(1);

  const getStartAndEndDate = useCallback(() => {
    let today = startDate ? new Date(startDate) : new Date();
    const firstDayOfLastMonth = startDate
      ? new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1))
      : new Date(Date.UTC(today.getFullYear(), today.getMonth() - 1, 1));
    const lastDayOfLastMonth = startDate
      ? new Date(Date.UTC(today.getFullYear(), today.getMonth() + 1, 0))
      : new Date(Date.UTC(today.getFullYear(), today.getMonth(), 0));
    let firstDay = firstDayOfLastMonth.toISOString().split("T")[0];
    let lastDay = lastDayOfLastMonth.toISOString().split("T")[0];
    return { firstDay, lastDay };
  }, [startDate]);

  // Step 1: Fetch Vendor ID
  const fetchVendorIdByLocation = useCallback(async () => {
    try {
      const vendorData = await functions.get(`/vendor/location/${location}`);
      const id = vendorData?.data[0]?.vendorId;
      setVendorId(id);
      return id;
    } catch (error) {
      console.error("Vendor ID fetch error:", error);
      throw error;
    }
  }, [location]);

  // Step 2: Fetch Invoice Summary and Description
  const fetchInvoiceSummaryAndDescription = useCallback(
    async (vendorId) => {
      try {
        const { firstDay, lastDay } = getStartAndEndDate();
        const res = await functions.get(
          `/invoice?locationId=${location}&vendorId=${vendorId}&startDate=${firstDay}&endDate=${lastDay}&mealTypeId=${mealType}`
        );
        if (res?.data) {
          setInvoiceSummaryData(res?.data);
          setInvoiceDescriptionData(res?.data?.invoiceDescription);
        }
        // console.log("Fetched Invoice Summary and Description:", res);
        return res;
      } catch (error) {
        console.error("Invoice Summary fetch error:", error);
        throw error;
      }
    },
    [getStartAndEndDate, location, mealType]
  );

  // Step 3: Fetch Invoice Summary Data
  const fetchInvoiceSummaryData = useCallback(
    async (vendorId) => {
      try {
        // console.log("Fetching Invoice Summary Data for Vendor ID:", vendorId);
        const res = await functions.get(`/invoice/summary/${vendorId}`);
        if (res?.data) {
          const price =
            mealType === 1
              ? res?.data?.vendorBreakfastPlatePrice
              : res?.data?.vendorDinnerPlatePrice;
          const minG =
            mealType === 1
              ? res?.data?.vendorBreakfastMinGuarantee
              : res?.data?.vendorDinnerMinGuarantee;
          setPerPlatePrice(price);
          setMinGuarantee(minG);
          setInvoiceSummaryData(res?.data);
        }
      } catch (error) {
        console.error("Invoice Summary fetch error:", error);
      }
    },
    [mealType]
  );

  // Step 4: Fetch List API Data by Location
  const fetchListApiDataByLocation = useCallback(
    async (vendorId) => {
      try {
        const { firstDay, lastDay } = getStartAndEndDate();
        const res = await functions.get(
          `/invoice/details?mealTypeId=${mealType}&locationId=${location}&startDate=${firstDay}&endDate=${lastDay}&vendorId=${vendorId}`
        );
        if (res?.data) {
          // console.log("Fetched List API Data by Location:131", res.data);
          setInvoiceDescriptionData(res?.data);
          // setInvoiceDescriptionByLocation(res.data);
          // setInvoiceDetailsByLocation(res?.data);
        }
      } catch (error) {
        console.error("List API Data by Location fetch error:", error);
        setInvoiceDescriptionByLocation(null);
      }
    },
    [getStartAndEndDate, location, mealType]
  );

  // Step 5: Fetch List API Data (Reports)
  const fetchListApiData = useCallback(async () => {
    try {
      const { firstDay, lastDay } = getStartAndEndDate();
      const res = await functions.get(
        `/reports?mealTypeId=${mealType}&locationId=${location}&startDate=${firstDay}&endDate=${lastDay}`
      );
      if (res?.data) {
        setInvoiceDescriptionData(res.data);
      }
    } catch (error) {
      console.error("Reports fetch error:", error);
      setInvoiceDescriptionData([]);
    }
  }, [getStartAndEndDate, location, mealType]);

  // Step 6: Master Function to Call Everything Once
  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    try {
      const id = await fetchVendorIdByLocation(); // First fetch vendor ID
      if (id) {
        try {
          // Fetch Invoice Summary and Description
          let res = await fetchInvoiceSummaryAndDescription(id);
          if (res?.data) {
            const price =
              mealType === 1
                ? res?.data?.invoiceSummary[0]?.vendorBreakfastPlatePrice
                : res?.data?.invoiceSummary[0]?.vendorDinnerPlatePrice;
            const minG =
              mealType === 1
                ? res?.data?.invoiceSummary[0]?.vendorBreakfastMinGuarantee
                : res?.data?.invoiceSummary[0]?.vendorDinnerMinGuarantee;
            // console.log("fetch invoice summary and description", res?.data);
            setPerPlatePrice(price);
            setMinGuarantee(minG);
            setInvoiceSummaryData(res?.data);
            setInvoiceDetailsByLocation(res?.data?.invoiceDescription);
          }
        } catch (error) {
          if (mealType === 2 && location !== 1) {
            // Fetch additional data for this specific condition
            await Promise.all([
              fetchInvoiceSummaryData(id),
              fetchListApiDataByLocation(id),
            ]);
          } else {
            await Promise.all([
              fetchInvoiceSummaryData(id),
              fetchListApiData(id),
            ]);
          }
          console.error("Error fetching invoice summary or data:", error);
        }
      } else {
        console.error("No Vendor ID found.");
        setInvoiceDescriptionData([]);
        setInvoiceSummaryData(null);
        setInvoiceDetailsByLocation(null);
        setPerPlatePrice(0);
        setMinGuarantee(0);
        return;
      }
    } catch (error) {
      console.error("fetchAllData error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    fetchVendorIdByLocation,
    fetchInvoiceSummaryAndDescription,
    fetchInvoiceSummaryData,
    fetchListApiDataByLocation,
    fetchListApiData,
    location,
    mealType,
  ]);

  // Step 7: useEffect to call the master function only once
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleButtonClick = () => {};

  const handleExport = () => {
    const { firstDay, lastDay } = getStartAndEndDate();
    const input = document.getElementById("pdf-content");

    setLoading(true);

    const logoWidth = 30;
    const logoHeight = 15;

    const loadLogoImage = (url) =>
      new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => resolve(img);
        img.src = url;
      });

    loadLogoImage(logoUrl).then((logoImg) => {
      html2canvas(input, { scale: 2 }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const imgProps = {
          width: canvas.width,
          height: canvas.height,
        };

        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const padding = 10;

        const contentWidth = pageWidth - padding * 2;
        const contentHeight = (imgProps.height * contentWidth) / imgProps.width;

        const imgHeightPx = (canvas.height * contentWidth) / canvas.width;
        const pageHeightPx = pageHeight - padding * 2 - logoHeight - 10;

        let position = 0;
        let pageCount = 0;

        const canvasImage = new Image();
        canvasImage.src = imgData;

        canvasImage.onload = () => {
          while (position < imgHeightPx) {
            if (pageCount > 0) pdf.addPage();

            // Logo top-left
            pdf.addImage(
              logoImg,
              "PNG",
              padding,
              padding,
              logoWidth,
              logoHeight
            );

            // INVOICE text top-right
            pdf.setFontSize(25);
            pdf.setFont("helvetica", "bold");
            const invoiceText = "INVOICE";
            const invoiceTextWidth = pdf.getTextWidth(invoiceText);
            pdf.text(
              invoiceText,
              pageWidth - padding - invoiceTextWidth,
              padding + logoHeight - 2
            );

            // Slice content for this page
            const yOffset = (position * canvas.height) / imgHeightPx;
            const pageCanvas = document.createElement("canvas");
            const pageCtx = pageCanvas.getContext("2d");

            pageCanvas.width = canvas.width;
            pageCanvas.height = (pageHeightPx * canvas.height) / imgHeightPx;

            pageCtx.drawImage(
              canvas,
              0,
              yOffset,
              canvas.width,
              pageCanvas.height,
              0,
              0,
              canvas.width,
              pageCanvas.height
            );

            const pageData = pageCanvas.toDataURL("image/png");
            const contentY = padding + logoHeight + 5;

            pdf.addImage(
              pageData,
              "PNG",
              padding,
              contentY,
              contentWidth,
              pageHeight - contentY - 15
            );

            // Footer
            const footerText = "Infojini Consulting Private Limited";
            pdf.setFontSize(10);
            pdf.setFont("helvetica", "normal");
            const textWidth = pdf.getTextWidth(footerText);
            const footerX = (pageWidth - textWidth) / 2;
            const footerY = pageHeight - 10;
            pdf.text(footerText, footerX, footerY);

            position += pageHeightPx;
            pageCount++;
          }

          pdf.save(
            `${getMealName(mealType)}-${getLocationName(location)}-${moment(
              firstDay
            ).format("MMM,YY")}.pdf`
          );
          setLoading(false);
        };
      });
    });
  };

  const breadcrumbItems = [{ label: "Home", path: "/cms/dashboard" }];

  // console.log("invoiceSummaryData", invoiceSummaryData);

  return (
    <div className="Invoice">
      <CustomBreadcrumbs items={breadcrumbItems} currentLabel="Invoice" />
      <Box className="Invoice-container">
        <ReportFilter
          name={`Invoice`}
          handleButtonClick={handleButtonClick}
          onClick={handleExport}
          tempStartDate={tempStartDate}
          tempEndDate={tempEndDate}
          setTempStartDate={setTempStartDate}
          setTempEndDate={setTempEndDate}
          startDate={startDate}
          endDate={endDate}
          btnText="Download"
        />
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={375}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box id="pdf-content">
            <InvoiceSummary
              invoiceSummaryData={invoiceSummaryData}
              setInvoiceSummaryData={setInvoiceSummaryData}
              invoiceDescriptionData={invoiceDescriptionData}
              vendorIdByLocation={vendorIdByLocation}
              chargedPlateCount={chargedPlateCount}
              totalInvoice={totalInvoice}
              ApiStatus={ApiStatus}
              startDate={startDate}
              mealType={mealType}
              perPlatePrice={perPlatePrice}
              minGuarantee={minGuarantee}
              location={location}
            />

            <InvoiceDescription
              vendorId={vendorId}
              invoiceSummaryData={invoiceSummaryData}
              // invoiceDetailsByLocation={invoiceDetailsByLocation}
              invoiceDescriptionData={invoiceDescriptionData}
              chargedPlateCount={chargedPlateCount}
              totalInvoice={totalInvoice}
              totalMealConsumed={totalMealConsumed}
              setChargedPlateCount={setChargedPlateCount}
              setTotalInvoice={setTotalInvoice}
              setTotalMealConsumed={setTotalMealConsumed}
              mealType={mealType}
              location={location}
              startDate={startDate}
              perPlatePrice={perPlatePrice}
              minGuarantee={minGuarantee}
              invoiceDescriptionByLocation={invoiceDescriptionDataByLocation}
            />
          </Box>
        )}
      </Box>
      <Backdrop
        open={loading}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default InvoicePage;
